/**
 * arrowGeometry — pure functions for computing the shaft and arrowhead
 * geometry of the fortify trail arrow.
 *
 * The design contract:
 *   - The arrow TIP sits exactly on the OUTER CIRCUMFERENCE of the
 *     destination troop-count badge, on the side facing the source.
 *   - The arrowhead is a triangle whose point (the tip) sits on the badge
 *     edge.  The base of the triangle is pulled back from the tip by
 *     `arrowSize` along the direction vector, so the head points into the
 *     badge without overlapping its interior.
 *   - The shaft LINE runs from the source end to the BASE of the
 *     arrowhead (not all the way to the tip), so the line does not poke
 *     through the rendered polygon.
 *
 * Geometry summary (all coordinates in SVG viewBox units):
 *   unitDir  = (dest − src) / |dest − src|
 *   tipPt    = destBadgeCenter − unitDir × destBadgeRadius
 *   basePt   = tipPt − unitDir × arrowSize
 *   shaftEnd = basePt   (line x2/y2)
 *
 * No React-Native, no SVG imports — fully pure so vitest can execute it.
 */

export interface Point {
  x: number
  y: number
}

export interface ArrowGeometry {
  /** Start of the shaft (source territory centre). */
  shaftFrom: Point
  /** End of the shaft — the base of the arrowhead. The line stops HERE
   *  so the drawn stroke does not peek through the arrowhead polygon. */
  shaftTo: Point
  /**
   * SVG `points` string for the triangular arrowhead polygon.
   * Format: "tipX,tipY leftX,leftY rightX,rightY"
   */
  arrowPoints: string
  /** The tip point (lies on the badge circumference). Exposed for tests. */
  tip: Point
}

/**
 * How far the shaft starts from the source badge centre. Most of the badge
 * radius: the line clears the source troop number's digits without leaving
 * a visible gap. Shared by every arrow (fortify trail, snapshot fortify,
 * snapshot attack).
 */
export function sourceArrowInset(badgeRadius: number, _trailWidth?: number): number {
  return badgeRadius * 0.7
}

/**
 * Where the arrow tip stops short of the destination badge centre: inside
 * the badge circle, just clear of the digits, so the head sits close to the
 * number it points at. Pass as `toBadgeRadius`.
 */
export function targetArrowInset(badgeRadius: number): number {
  return badgeRadius * 0.75
}

/** Share of the source-to-tip span the start gap may take on short hops. */
const MAX_INSET_SHARE = 0.3
/** Share of what remains the head may take, so a tail always shows. */
const MAX_HEAD_SHARE = 0.55

/**
 * Compute shaft endpoints and arrowhead polygon for a fortify arrow.
 *
 * @param from          Centre of the SOURCE troop-count badge (badgePositionById
 *                      value, PoI/anchor-corrected; centroid only as fallback).
 * @param toBadgeCenter Centre of the destination troop-count badge
 *                      (badgePositionById value, PoI-corrected).
 * @param toBadgeRadius Radius of the destination badge circle in viewBox units
 *                      (MapView's `badgeRadius` = Math.max(2.5, mapW * 0.022)).
 * @param arrowSize     Length of the arrowhead (typically trailWidth × 4).
 * @param fromInset     Gap between the source badge centre and the start of
 *                      the shaft, so the line clears the source troop number
 *                      instead of starting under it. On short hops the gap
 *                      and the head both shrink so a tail always shows.
 */
export function computeArrowGeometry(
  from: Point,
  toBadgeCenter: Point,
  toBadgeRadius: number,
  arrowSize: number,
  fromInset = 0,
): ArrowGeometry {
  const dx = toBadgeCenter.x - from.x
  const dy = toBadgeCenter.y - from.y
  const len = Math.hypot(dx, dy) || 1
  // Unit vector pointing from source towards destination.
  const ux = dx / len
  const uy = dy / len
  // On short hops the fixed gap and head would swallow the whole span and
  // leave no tail. Cap both as shares of the span instead: the gap takes at
  // most 30% of source-to-tip, the head at most 55% of what is left, so the
  // shaft is always at least ~30% of the span.
  const tipDist = Math.max(0, len - toBadgeRadius)
  const inset = Math.max(0, Math.min(fromInset, tipDist * MAX_INSET_SHARE))
  const headLen = Math.min(arrowSize, (tipDist - inset) * MAX_HEAD_SHARE)
  const shaftFrom: Point = { x: from.x + ux * inset, y: from.y + uy * inset }

  // Tip sits on the badge edge facing the source.
  const tip: Point = {
    x: toBadgeCenter.x - ux * toBadgeRadius,
    y: toBadgeCenter.y - uy * toBadgeRadius,
  }

  // Base of the arrowhead — pulled back from the tip by arrowSize along
  // the same direction, so the triangle points INTO the badge.
  const baseX = tip.x - ux * headLen
  const baseY = tip.y - uy * headLen

  // Perpendicular unit vector for the arrowhead base spread.
  const px = -uy
  const py = ux
  const half = headLen * 0.55

  const leftX = baseX + px * half
  const leftY = baseY + py * half
  const rightX = baseX - px * half
  const rightY = baseY - py * half

  return {
    shaftFrom,
    shaftTo: { x: baseX, y: baseY },
    arrowPoints: `${tip.x},${tip.y} ${leftX},${leftY} ${rightX},${rightY}`,
    tip,
  }
}
